import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Switch,
  message,
  Modal,
  Form,
  Input,
  Select,
  Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../utils/api';

const { Option } = Select;
const { confirm } = Modal;

const PopupList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [popupList, setPopupList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});

  // 获取弹窗列表
  const fetchPopupList = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      };

      const response = await api.get('/admin/popups/list', params);
      if (response.code === 200) {
        setPopupList(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.total,
        });
      } else {
        message.error(response.message || '获取弹窗列表失败');
      }
    } catch (error) {
      message.error('获取弹窗列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPopupList();
  }, [pagination.current, pagination.pageSize, filters]);

  // 处理表格分页变化
  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
    });
  };

  // 处理搜索
  const handleSearch = (values) => {
    const newFilters = {};
    if (values.keyword) {
      newFilters.keyword = values.keyword;
    }
    if (values.status !== undefined && values.status !== '' && values.status !== null) {
      newFilters.status = values.status;
    }
    setFilters(newFilters);
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  // 重置搜索
  const handleReset = () => {
    setFilters({});
    setPagination({
      ...pagination,
      current: 1,
    });
    form.resetFields();
  };

  // 跳转到新增页面
  const goToCreate = () => {
    navigate('/popups/create');
  };

  // 跳转到编辑页面
  const goToEdit = (id) => {
    navigate(`/popups/edit/${id}`);
  };

  // 删除弹窗
  const handleDelete = (popup) => {
    confirm({
      title: '确认删除',
      content: `确定要删除弹窗"${popup.title}"吗？`,
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/popups/${popup.id}`);
          if (response.code === 200) {
            message.success('删除成功');
            fetchPopupList();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败: ' + error.message);
        }
      },
    });
  };

  // 更新状态
  const updateStatus = async (popup, status) => {
    try {
      const response = await api.put(`/admin/popups/${popup.id}`, { 
        status: status ? 'active' : 'inactive' 
      });
      if (response.code === 200) {
        message.success('状态更新成功');
        fetchPopupList();
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败: ' + error.message);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '内容类型',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 100,
      render: (image_url) => (
        <Tag color={image_url ? 'blue' : 'green'}>
          {image_url ? '图片' : '文字'}
        </Tag>
      ),
    },
    {
      title: '频率类型',
      dataIndex: 'frequency_type',
      key: 'frequency_type',
      width: 120,
      render: (frequency_type, record) => {
        const typeMap = {
          always: '每次都弹',
          total: `总共${record.frequency_limit}次`,
          daily: `每天${record.frequency_limit}次`
        };
        return <Tag>{typeMap[frequency_type] || frequency_type}</Tag>;
      },
    },
    {
      title: '链接类型',
      dataIndex: 'link_type',
      key: 'link_type',
      width: 100,
      render: (link_type) => {
        const typeMap = {
          none: '无链接',
          internal: '内部链接',
          external: '外部链接'
        };
        return typeMap[link_type] || link_type;
      },
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
    },
    {
      title: '生效时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 180,
      render: (start_time) => start_time ? dayjs(start_time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 'active'}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => updateStatus(record, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => goToEdit(record.id)}
            size="small"
          >
            编辑
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="弹窗管理"
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={goToCreate}
        >
          新增弹窗
        </Button>
      }
    >
      {/* 搜索表单 */}
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        initialValues={{ status: '' }}
        style={{ marginBottom: 16 }}
      >
        <Form.Item name="keyword" label="关键词">
          <Input placeholder="弹窗标题" />
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select style={{ width: 120 }}>
            <Option value="">全部</Option>
            <Option value="active">启用</Option>
            <Option value="inactive">禁用</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 弹窗列表 */}
      <Table
        columns={columns}
        dataSource={popupList}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </Card>
  );
};

export default PopupList;
